import { AxiosError } from "axios";
import AUTHENTICATED_USER_KEY from "../constants/AuthConstants";
import { AuthenticatedUser, AuthError, Role, Status } from "../types/AuthTypes";
import { Administrator, Learner } from "../types/UserTypes";
import {
  getLocalStorageObjProperty,
  setLocalStorageObjProperty,
} from "../utils/LocalStorageUtils";
import baseAPIClient from "./BaseAPIClient";

const login = async (
  email: string,
  password: string,
  attemptedRole: Role,
): Promise<AuthenticatedUser | null> => {
  try {
    const { data } = await baseAPIClient.post("/auth/login", {
      email,
      password,
      attemptedRole,
    });
    // Store refreshToken in localStorage
    if (data.refreshToken) {
      setLocalStorageObjProperty(
        AUTHENTICATED_USER_KEY,
        "refreshToken",
        data.refreshToken,
      );
    }
    return data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data.error, {
        cause: error.response?.data as AuthError,
      });
    }
    return null;
  }
};

const logout = async (userId: string | undefined): Promise<boolean> => {
  const bearerToken = `Bearer ${getLocalStorageObjProperty(
    AUTHENTICATED_USER_KEY,
    "accessToken",
  )}`;
  try {
    localStorage.removeItem(AUTHENTICATED_USER_KEY);
    await baseAPIClient.post(
      `/auth/logout/${userId}`,
      {},
      { headers: { Authorization: bearerToken } },
    );
    return true;
  } catch (error) {
    return false;
  }
};

const signup = async (
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  role: string,
): Promise<AuthenticatedUser | null> => {
  try {
    const { data } = await baseAPIClient.post("/auth/signup", {
      firstName,
      lastName,
      email,
      password,
      role,
    });
    // Store refreshToken in localStorage
    if (data.refreshToken) {
      setLocalStorageObjProperty(
        AUTHENTICATED_USER_KEY,
        "refreshToken",
        data.refreshToken,
      );
    }
    return data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data.error);
    }
    return null;
  }
};

const resetPassword = async (email: string): Promise<boolean> => {
  try {
    await baseAPIClient.post(`/auth/resetPassword/${email}`, {});
    return true;
  } catch (error) {
    return false;
  }
};

const updateTemporaryPassword = async (
  email: string,
  newPassword: string,
  role: Role,
): Promise<boolean> => {
  const bearerToken = `Bearer ${getLocalStorageObjProperty(
    AUTHENTICATED_USER_KEY,
    "accessToken",
  )}`;
  try {
    await baseAPIClient.post(
      `/auth/updateTemporaryPassword`,
      { newPassword },
      { headers: { Authorization: bearerToken } },
    );
    const newAuthenticatedUser = await login(email, newPassword, role);
    if (!newAuthenticatedUser) {
      throw new Error("Unable to authenticate user after logging in.");
    }
    setLocalStorageObjProperty(
      AUTHENTICATED_USER_KEY,
      "accessToken",
      newAuthenticatedUser.accessToken,
    );
    return true;
  } catch (error) {
    return false;
  }
};

const changePassword = async (
  email: string,
  newPassword: string,
  role: Role,
): Promise<boolean> => {
  const bearerToken = `Bearer ${getLocalStorageObjProperty(
    AUTHENTICATED_USER_KEY,
    "accessToken",
  )}`;
  try {
    await baseAPIClient.put(
      `/auth/changePassword`,
      { newPassword },
      { headers: { Authorization: bearerToken } },
    );
    const newAuthenticatedUser = await login(email, newPassword, role);
    if (!newAuthenticatedUser) {
      throw new Error("Unable to authenticate user after logging in.");
    }
    setLocalStorageObjProperty(
      AUTHENTICATED_USER_KEY,
      "accessToken",
      newAuthenticatedUser.accessToken,
    );
    return true;
  } catch (error) {
    return false;
  }
};

const changePasswordFromId = async (
  authId: string,
  newPassword: string,
  requestedTime: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    const { data } = await baseAPIClient.put(`/auth/changePasswordFromId`, {
      authId,
      newPassword,
      requestedTime,
    });
    return {
      success: true,
      message: data.message || "Password updated successfully!",
    };
  } catch (error) {
    return error instanceof AxiosError
      ? { success: false, message: error.response?.data.error }
      : { success: false, message: "Failed to change password." };
  }
};

const updateUserStatus = async (newStatus: Status): Promise<boolean> => {
  const bearerToken = `Bearer ${getLocalStorageObjProperty(
    AUTHENTICATED_USER_KEY,
    "accessToken",
  )}`;
  try {
    await baseAPIClient.post(
      `/auth/updateUserStatus`,
      { status: newStatus },
      { headers: { Authorization: bearerToken } },
    );
    return true;
  } catch (error) {
    return false;
  }
};

// for testing only, refresh does not need to be exposed in the client
const refresh = async (): Promise<boolean> => {
  try {
    const refreshToken = getLocalStorageObjProperty(
      AUTHENTICATED_USER_KEY,
      "refreshToken",
    );
    const { data } = await baseAPIClient.post("/auth/refresh", {
      refreshToken,
    });
    setLocalStorageObjProperty(
      AUTHENTICATED_USER_KEY,
      "accessToken",
      data.accessToken,
    );
    // Update refreshToken in localStorage
    if (data.refreshToken) {
      setLocalStorageObjProperty(
        AUTHENTICATED_USER_KEY,
        "refreshToken",
        data.refreshToken,
      );
    }
    return true;
  } catch (error) {
    // If refresh fails, clear authentication data and redirect to login
    localStorage.removeItem(AUTHENTICATED_USER_KEY);
    window.location.href = "/";
    return false;
  }
};

const isUserVerified = async (
  email: string,
  accessToken: string,
): Promise<boolean> => {
  const bearerToken = `Bearer ${accessToken}`;

  try {
    const { data } = await baseAPIClient.post(
      `/auth/isUserVerified/${email}`,
      {},
      { headers: { Authorization: bearerToken } },
    );
    return data.isVerified;
  } catch (error) {
    return false;
  }
};

const inviteAdmin = async (
  firstName: string,
  lastName: string,
  email: string,
): Promise<Administrator | null> => {
  const bearerToken = `Bearer ${getLocalStorageObjProperty(
    AUTHENTICATED_USER_KEY,
    "accessToken",
  )}`;

  try {
    const { data } = await baseAPIClient.post(
      `/auth/inviteAdmin`,
      { firstName, lastName, email },
      { headers: { Authorization: bearerToken } },
    );
    return data;
  } catch (error) {
    return null;
  }
};

const inviteLearner = async (
  firstName: string,
  lastName: string,
  email: string,
): Promise<Learner | null> => {
  const bearerToken = `Bearer ${getLocalStorageObjProperty(
    AUTHENTICATED_USER_KEY,
    "accessToken",
  )}`;

  try {
    const { data } = await baseAPIClient.post(
      `/auth/inviteLearner`,
      { firstName, lastName, email },
      { headers: { Authorization: bearerToken } },
    );
    return data;
  } catch (error) {
    return null;
  }
};

export default {
  login,
  logout,
  signup,
  resetPassword,
  updateTemporaryPassword,
  changePassword,
  changePasswordFromId,
  updateUserStatus,
  refresh,
  isUserVerified,
  inviteAdmin,
  inviteLearner,
};
